// 로또 관련 유틸리티 함수들
import * as tf from '@tensorflow/tfjs';
import { Matrix } from 'ml-matrix';

// ============= 개발 환경 전용 로깅 유틸리티 =============
const devLog = (...args) => {
  if (import.meta.env.DEV) console.log(...args);
};

const devWarn = (...args) => {
  if (import.meta.env.DEV) console.warn(...args);
};

const devError = (...args) => {
  if (import.meta.env.DEV) console.error(...args);
};

// ============= TensorFlow.js 최적화 설정 =============
let tfInitialized = false;

// TensorFlow.js 초기화 및 최적화 설정
const initializeTensorFlow = async () => {
  if (tfInitialized || typeof window === 'undefined') return;
  
  try {
    // TensorFlow.js가 준비될 때까지 대기
    await tf.ready();
    
    devLog('🚀 TensorFlow.js 초기화 완료');
    devLog(`사용 중인 백엔드: ${tf.getBackend()}`);
    
    // 안전한 환경 설정만 적용
    try {
      // WebGL 백엔드 최적화
      tf.ENV.set('WEBGL_PACK', true);
      devLog('✅ WebGL 패킹 최적화 활성화');
    } catch (e) {
      devLog('ℹ️ WebGL 패킹 설정 건너뜀');
    }
    
    try {
      // 메모리 최적화
      tf.ENV.set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0.5);
      devLog('✅ WebGL 메모리 최적화 활성화');
    } catch (e) {
      devLog('ℹ️ WebGL 메모리 설정 건너뜀');
    }
    
    // 사용 가능한 백엔드 확인 (안전하게)
    try {
      const currentBackend = tf.getBackend();
      if (currentBackend) {
        devLog(`📊 현재 백엔드: ${currentBackend}`);
      }
    } catch (e) {
      devLog('ℹ️ 백엔드 정보 확인 건너뜀');
    }
    
    tfInitialized = true;
    devLog('🎯 TensorFlow.js 최적화 설정 완료');
    
  } catch (error) {
    devWarn('⚠️ TensorFlow.js 초기화 중 오류:', error.message);
    devLog('기본 설정으로 계속 진행합니다.');
  }
};

// 브라우저 환경에서 초기화 실행
if (typeof window !== 'undefined') {
  initializeTensorFlow();
}

// ============= 모델 캐싱 시스템 =============
class ModelCache {
  constructor() {
    this.cache = new Map();
    this.lastDataHash = null;
    this.lastUpdateTime = null;
    this.cacheExpireTime = 10 * 60 * 1000; // 10분
    this.dataChangeThreshold = 5; // 데이터 변화 5개 이상 시 재학습
  }

  // 데이터 해시 생성 (간단한 체크섬)
  generateDataHash(data) {
    const recentData = data.slice(-100); // 최근 100회차만 사용
    return recentData.map(d => `${d.round}-${d.numbers.join(',')}`).join('|');
  }

  // 캐시가 유효한지 확인
  isCacheValid(dataHash) {
    const now = Date.now();
    
    // 캐시가 비어있는지 확인
    if (!this.lastUpdateTime) {
      return false;
    }

    // 시간 기반 만료 확인: 현재시간이 만료시간을 넘었는지 확인
    const expiryTime = this.lastUpdateTime + this.cacheExpireTime;
    if (now >= expiryTime) {
      return false;
    }

    // 데이터가 변경되었는지 확인
    if (this.lastDataHash !== dataHash) {
      return false;
    }

    return true;
  }

  // 모델 캐시에서 가져오기
  getModel(key, dataHash) {
    if (!this.isCacheValid(dataHash)) {
      this.clearCache();
      return null;
    }
    return this.cache.get(key);
  }

  // 모델 캐시에 저장
  setModel(key, model, dataHash) {
    this.cache.set(key, model);
    this.lastDataHash = dataHash;
    this.lastUpdateTime = Date.now();
  }

  // 캐시 정리
  clearCache() {
    this.cache.forEach((model, key) => {
      try {
        if (model && model.dispose) {
          model.dispose();
        }
      } catch (error) {
        devWarn(`모델 정리 중 오류 (${key}):`, error);
      }
    });
    this.cache.clear();
    this.lastDataHash = null;
    this.lastUpdateTime = null;
  }

  // 캐시 상태 정보
  getCacheInfo() {
    const now = Date.now();
    // 올바른 남은 시간 계산: (생성시간 + 만료시간) - 현재시간
    const expiryTime = this.lastUpdateTime + this.cacheExpireTime;
    const timeLeft = this.lastUpdateTime ? Math.max(0, expiryTime - now) : 0;
    
    return {
      cacheSize: this.cache.size,
      timeLeft: Math.round(timeLeft / 1000), // 초 단위
      hasCache: this.cache.size > 0 && timeLeft > 0, // 시간이 남아있을 때만 캐시 유효
      lastUpdate: this.lastUpdateTime
    };
  }
}

// 전역 모델 캐시 인스턴스
const modelCache = new ModelCache();

// ============= 비동기 처리 유틸리티 =============

/**
 * requestIdleCallback의 폴리필
 */
const requestIdleCallback = window.requestIdleCallback || function(callback) {
  const start = Date.now();
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining() {
        return Math.max(0, 50 - (Date.now() - start));
      }
    });
  }, 1);
};

/**
 * 작업을 여러 프레임에 걸쳐 분할 실행 (강화된 UI 블로킹 방지)
 * @param {Function} task - 실행할 작업 함수
 * @param {number} priority - 우선순위 (높을수록 더 자주 yield)
 */
const scheduleWork = (task, priority = 1) => {
  return new Promise((resolve, reject) => {
    const runBatch = () => {
      try {
        const result = task();
        if (result && result.then) {
          // Promise가 반환된 경우
          result.then(resolve).catch(reject);
        } else {
          resolve(result);
        }
      } catch (error) {
        reject(error);
      }
    };

    // 우선순위에 따라 다른 스케줄링 방법 사용
    if (priority >= 3) {
      // 높은 우선순위: 즉시 yield
      setTimeout(runBatch, 0);
    } else if (priority >= 2) {
      // 중간 우선순위: requestIdleCallback 사용
      if (window.requestIdleCallback) {
        requestIdleCallback(runBatch, { timeout: 50 });
      } else {
        setTimeout(runBatch, 16); // 60fps 기준
      }
    } else {
      // 낮은 우선순위: 브라우저가 유휴할 때
      if (window.requestIdleCallback) {
        requestIdleCallback(runBatch);
      } else {
        setTimeout(runBatch, 1);
      }
    }
  });
};

/**
 * 지연 실행 유틸리티
 * @param {number} ms - 지연 시간 (밀리초)
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 실제 로또 데이터 로드
 * @returns {Promise<Array>} 로또 데이터 배열
 */
export async function loadLottoData() {
  try {
    // 개발 모드와 프로덕션 모드에서 다른 경로 사용
    const isDev = import.meta.env.DEV
    const dataUrl = isDev 
      ? '/data/lotto-data.json'  // 개발 모드: Vite 정적 파일 서빙
      : '/data/lotto-data.json'  // 프로덕션 모드: 빌드된 public 폴더
    
    devLog(`로또 데이터 로드 시도: ${dataUrl}`)
    
    const response = await fetch(dataUrl)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('올바르지 않은 데이터 형식입니다.')
    }
    
    // 데이터 형식 검증 및 정규화
    const validData = data.map(item => ({
      round: item.round,
      date: item.date,
      numbers: Array.isArray(item.numbers) ? item.numbers.sort((a, b) => a - b) : [],
      bonus: item.bonus,
      firstWinamnt: item.firstWinamnt || 0,
      firstPrzwnerCo: item.firstPrzwnerCo || 0,
      totSellamnt: item.totSellamnt || 0
    })).filter(item => 
      item.round && 
      item.date && 
      Array.isArray(item.numbers) && 
      item.numbers.length === 6 && 
      item.bonus
    )
    
    devLog(`로또 데이터 로드 성공: ${validData.length}개 회차`)
    return validData
    
  } catch (error) {
    devError('로또 데이터 로드 실패:', error.message)
    devWarn('샘플 데이터를 사용합니다.')
    return generateSampleLottoData(100)
  }
}

/**
 * 샘플 로또 데이터 생성 (백업용)
 * @param {number} count - 생성할 회차 수
 * @returns {Array} 로또 데이터 배열
 */
export function generateSampleLottoData(count = 100) {
  const data = []
  const startDate = new Date('2024-01-01')
  
  for (let i = 1; i <= count; i++) {
    const numbers = generateRandomLottoNumbers()
    const bonus = generateRandomNumber(1, 45, numbers)
    const date = new Date(startDate.getTime() + (i - 1) * 7 * 24 * 60 * 60 * 1000)
    
    data.push({
      round: i,
      date: date.toISOString().split('T')[0],
      numbers: numbers.sort((a, b) => a - b),
      bonus: bonus,
      firstWinamnt: Math.floor(Math.random() * 2000000000) + 1000000000,
      firstPrzwnerCo: Math.floor(Math.random() * 10) + 1,
      totSellamnt: Math.floor(Math.random() * 100000000000) + 50000000000
    })
  }
  
  return data
}

/**
 * 랜덤 로또 번호 6개 생성
 * @returns {Array} 6개의 숫자 배열
 */
export function generateRandomLottoNumbers() {
  const numbers = new Set()
  
  while (numbers.size < 6) {
    const num = Math.floor(Math.random() * 45) + 1
    numbers.add(num)
  }
  
  return Array.from(numbers)
}

/**
 * 특정 범위에서 제외 번호를 피해 랜덤 번호 생성
 * @param {number} min - 최소값
 * @param {number} max - 최대값
 * @param {Array} exclude - 제외할 번호들
 * @returns {number} 랜덤 번호
 */
export function generateRandomNumber(min, max, exclude = []) {
  let num
  do {
    num = Math.floor(Math.random() * (max - min + 1)) + min
  } while (exclude.includes(num))
  
  return num
}

/**
 * 번호별 출현 빈도 계산
 * @param {Array} lottoData - 로또 데이터 배열
 * @returns {Object} 번호별 빈도 객체
 */
export function calculateFrequency(lottoData) {
  const frequency = {}
  
  // 1-45까지 초기화
  for (let i = 1; i <= 45; i++) {
    frequency[i] = 0
  }
  
  lottoData.forEach(draw => {
    draw.numbers.forEach(number => {
      frequency[number]++
    })
  })
  
  return frequency
}

/**
 * 종합 통계 계산
 * @param {Array} lottoData - 로또 데이터 배열
 * @returns {Object} 통계 객체
 */
export function calculateStats(lottoData) {
  const frequency = calculateFrequency(lottoData)
  let oddCount = 0
  let evenCount = 0
  let lowCount = 0  // 1-22
  let highCount = 0 // 23-45
  let totalSum = 0
  let consecutiveCount = 0
  
  lottoData.forEach(draw => {
    // 홀수/짝수 계산
    draw.numbers.forEach(number => {
      if (number % 2 === 0) {
        evenCount++
      } else {
        oddCount++
      }
      
      // 고저 번호 계산
      if (number <= 22) {
        lowCount++
      } else {
        highCount++
      }
      
      totalSum += number
    })
    
    // 연속 번호 체크
    const sortedNumbers = [...draw.numbers].sort((a, b) => a - b)
    for (let i = 0; i < sortedNumbers.length - 1; i++) {
      if (sortedNumbers[i + 1] - sortedNumbers[i] === 1) {
        consecutiveCount++
        break
      }
    }
  })
  
  const totalNumbers = lottoData.length * 6
  
  return {
    frequency,
    oddCount,
    evenCount,
    oddPercentage: (oddCount / totalNumbers) * 100,
    evenPercentage: (evenCount / totalNumbers) * 100,
    lowCount,
    highCount,
    lowPercentage: (lowCount / totalNumbers) * 100,
    highPercentage: (highCount / totalNumbers) * 100,
    averageSum: totalSum / lottoData.length,
    consecutivePercentage: (consecutiveCount / lottoData.length) * 100
  }
}

/**
 * Decision Tree 기반 번호 예측
 * @param {Array} historicalData - 과거 데이터
 * @returns {Array} 예측된 번호 6개
 */
export function predictWithDecisionTree(historicalData) {
  const stats = calculateStats(historicalData)
  const frequency = stats.frequency
  
  // 빈도 기반 가중치 계산
  const weights = {}
  const maxFreq = Math.max(...Object.values(frequency))
  const minFreq = Math.min(...Object.values(frequency))
  
  for (let i = 1; i <= 45; i++) {
    // 출현 빈도가 낮은 번호에 더 높은 가중치
    weights[i] = (maxFreq - frequency[i] + minFreq) / maxFreq
  }
  
  // 가중치 기반 번호 선택
  const selectedNumbers = new Set()
  
  while (selectedNumbers.size < 6) {
    const randomValue = Math.random()
    let cumulativeWeight = 0
    
    for (let i = 1; i <= 45; i++) {
      cumulativeWeight += weights[i] / 45
      if (randomValue <= cumulativeWeight && !selectedNumbers.has(i)) {
        selectedNumbers.add(i)
        break
      }
    }
    
    // 무한 루프 방지
    if (selectedNumbers.size < 6) {
      const remaining = []
      for (let i = 1; i <= 45; i++) {
        if (!selectedNumbers.has(i)) remaining.push(i)
      }
      if (remaining.length > 0) {
        selectedNumbers.add(remaining[Math.floor(Math.random() * remaining.length)])
      }
    }
  }
  
  return Array.from(selectedNumbers).sort((a, b) => a - b)
}

/**
 * Random Forest 앙상블 예측
 * @param {Array} historicalData - 과거 데이터
 * @param {number} trees - 트리 개수
 * @returns {Array} 예측된 번호 6개
 */
export function predictWithRandomForest(historicalData, trees = 10) {
  const predictions = []
  
  // 여러 트리에서 예측 수행
  for (let i = 0; i < trees; i++) {
    // 부트스트래핑을 위한 샘플 데이터
    const sampleData = []
    for (let j = 0; j < historicalData.length; j++) {
      const randomIndex = Math.floor(Math.random() * historicalData.length)
      sampleData.push(historicalData[randomIndex])
    }
    
    const treeResult = predictWithDecisionTree(sampleData)
    predictions.push(treeResult)
  }
  
  // 앙상블 결과 계산
  const numberVotes = {}
  for (let i = 1; i <= 45; i++) {
    numberVotes[i] = 0
  }
  
  predictions.forEach(prediction => {
    prediction.forEach(number => {
      numberVotes[number]++
    })
  })
  
  // 투표 결과를 기반으로 상위 6개 번호 선택
  const sortedNumbers = Object.entries(numberVotes)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 6)
    .map(([number]) => parseInt(number))
  
  return sortedNumbers.sort((a, b) => a - b)
}

/**
 * 신경망 기반 예측 (간단한 피드포워드)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Array} 예측된 번호 6개
 */
export function predictWithNeuralNetwork(historicalData) {
  const recentData = historicalData.slice(-20) // 최근 20회 데이터 사용
  const stats = calculateStats(recentData)
  
  // 패턴 분석
  const patterns = analyzePatterns(recentData)
  
  // 가중치 계산
  const weights = {}
  for (let i = 1; i <= 45; i++) {
    weights[i] = 
      (stats.frequency[i] || 0) * 0.3 + // 빈도 가중치
      (patterns.recentAppearance[i] || 0) * 0.2 + // 최근 출현 가중치
      (patterns.position[i] || 0) * 0.2 + // 위치 패턴 가중치
      Math.random() * 0.3 // 랜덤 요소
  }
  
  // 상위 가중치 번호들 중에서 선택
  const sortedByWeight = Object.entries(weights)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 12) // 상위 12개 후보
  
  // 후보 중에서 랜덤 선택
  const selectedNumbers = new Set()
  while (selectedNumbers.size < 6) {
    const randomIndex = Math.floor(Math.random() * sortedByWeight.length)
    selectedNumbers.add(parseInt(sortedByWeight[randomIndex][0]))
  }
  
  return Array.from(selectedNumbers).sort((a, b) => a - b)
}

/**
 * 패턴 분석
 * @param {Array} data - 분석할 데이터
 * @returns {Object} 패턴 분석 결과
 */
function analyzePatterns(data) {
  const recentAppearance = {}
  const position = {}
  
  // 초기화
  for (let i = 1; i <= 45; i++) {
    recentAppearance[i] = 0
    position[i] = 0
  }
  
  data.forEach((draw, index) => {
    const weight = (index + 1) / data.length // 최근일수록 높은 가중치
    
    draw.numbers.forEach((number, pos) => {
      recentAppearance[number] += weight
      position[number] += (pos + 1) * weight // 위치별 가중치
    })
  })
  
  return { recentAppearance, position }
}

/**
 * 통계 기반 빈도 분석 번호 생성
 * @param {Array} historicalData - 과거 데이터
 * @returns {Array} 예측된 번호 6개
 */
export function predictWithStatistics(historicalData) {
  const stats = calculateStats(historicalData)
  const frequency = stats.frequency
  
  // 출현 빈도가 평균보다 낮은 번호들을 우선 선택
  const avgFrequency = Object.values(frequency).reduce((a, b) => a + b) / 45
  const underperformingNumbers = []
  const normalNumbers = []
  
  for (let i = 1; i <= 45; i++) {
    if (frequency[i] < avgFrequency) {
      underperformingNumbers.push(i)
    } else {
      normalNumbers.push(i)
    }
  }
  
  // 저빈도 번호 4개, 정상 빈도 번호 2개 선택
  const selectedNumbers = []
  
  // 저빈도 번호에서 4개 선택
  for (let i = 0; i < 4 && underperformingNumbers.length > 0; i++) {
    const randomIndex = Math.floor(Math.random() * underperformingNumbers.length)
    selectedNumbers.push(underperformingNumbers.splice(randomIndex, 1)[0])
  }
  
  // 나머지는 전체에서 선택
  const allRemaining = [...underperformingNumbers, ...normalNumbers]
  while (selectedNumbers.length < 6 && allRemaining.length > 0) {
    const randomIndex = Math.floor(Math.random() * allRemaining.length)
    selectedNumbers.push(allRemaining.splice(randomIndex, 1)[0])
  }
  
  return selectedNumbers.sort((a, b) => a - b)
}

/**
 * 번호 조합의 적합성 점수 계산
 * @param {Array} numbers - 번호 조합
 * @param {Array} historicalData - 과거 데이터
 * @returns {number} 적합성 점수 (0-100)
 */
export function calculateFitnessScore(numbers, historicalData) {
  let score = 0
  const stats = calculateStats(historicalData)
  
  // 1. 홀수/짝수 비율 체크 (25점)
  const oddCount = numbers.filter(n => n % 2 === 1).length
  const idealOddRatio = 0.5 // 이상적인 홀수 비율
  const oddRatio = oddCount / 6
  score += 25 * (1 - Math.abs(oddRatio - idealOddRatio) * 2)
  
  // 2. 고저 번호 비율 체크 (25점)
  const lowCount = numbers.filter(n => n <= 22).length
  const idealLowRatio = 0.5
  const lowRatio = lowCount / 6
  score += 25 * (1 - Math.abs(lowRatio - idealLowRatio) * 2)
  
  // 3. 번호 합계 체크 (25점)
  const sum = numbers.reduce((a, b) => a + b, 0)
  const idealSum = stats.averageSum
  const sumDiff = Math.abs(sum - idealSum) / idealSum
  score += 25 * (1 - Math.min(sumDiff, 1))
  
  // 4. 연속 번호 체크 (25점)
  const sortedNumbers = [...numbers].sort((a, b) => a - b)
  let hasConsecutive = false
  for (let i = 0; i < sortedNumbers.length - 1; i++) {
    if (sortedNumbers[i + 1] - sortedNumbers[i] === 1) {
      hasConsecutive = true
      break
    }
  }
  
  // 연속 번호가 있으면 보너스 (통계적으로 자주 나타남)
  if (hasConsecutive && stats.consecutivePercentage > 30) {
    score += 25
  } else if (!hasConsecutive && stats.consecutivePercentage <= 30) {
    score += 25
  } else {
    score += 10
  }
  
  return Math.max(0, Math.min(100, score))
}

// NumberGenerator 컴포넌트에서 사용하는 함수들
export async function generateNumbersWithDecisionTree() {
  const realData = await loadLottoData()
  return predictWithDecisionTree(realData)
}

export async function generateNumbersWithRandomForest() {
  const realData = await loadLottoData()
  return predictWithRandomForest(realData)
}

export async function generateNumbersWithNeuralNetwork() {
  const realData = await loadLottoData()
  return predictWithNeuralNetwork(realData)
}

export async function generateNumbersWithFrequencyAnalysis() {
  const realData = await loadLottoData()
  return predictWithStatistics(realData)
}

// ============= 고급 AI 예측 함수들 =============

// ============= 예측 다양성 보장 시스템 =============

/**
 * 시간 기반 시드 생성기
 * Clean Code 원칙: 의미있는 변수명과 단일 책임
 */
const generateTimeSeed = () => {
  const now = new Date();
  const timeComponents = [
    now.getSeconds(),
    now.getMinutes(), 
    now.getHours(),
    now.getDate()
  ];
  return timeComponents.reduce((seed, component) => seed + component, 0);
};

/**
 * 시드를 기반으로 한 의사 랜덤 생성기
 * Context7 권장: 예측 가능하지만 다양한 결과
 */
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

/**
 * 다양한 입력 데이터 샘플링 전략
 * Clean Code: 함수의 단일 책임 원칙
 */
const generateVariedInputs = (historicalData, seed) => {
  const dataLength = historicalData.length;
  const random = seededRandom(seed);
  
  // 전략 1: 최근 데이터 + 랜덤 오프셋
  const recentWithOffset = Math.floor(random * 10) + 5; // 5-14개 최근 데이터
  const strategy1 = historicalData.slice(-recentWithOffset);
  
  // 전략 2: 주기적 샘플링 (매 N번째 데이터)
  const interval = Math.floor(seededRandom(seed + 1) * 5) + 2; // 2-6 간격
  const strategy2 = historicalData.filter((_, index) => index % interval === 0).slice(-20);
  
  // 전략 3: 특정 구간 집중 샘플링
  const sectionStart = Math.floor(seededRandom(seed + 2) * (dataLength - 50));
  const sectionLength = Math.floor(seededRandom(seed + 3) * 30) + 20; // 20-49개
  const strategy3 = historicalData.slice(sectionStart, sectionStart + sectionLength);
  
  return {
    recent: strategy1,
    periodic: strategy2,
    sectional: strategy3,
    randomChoice: Math.floor(random * 3) // 0, 1, 2 중 하나 선택
  };
};

/**
 * 예측에 랜덤성 주입
 * Clean Code: 명확한 함수명과 목적
 */
const injectRandomVariation = (baseNumbers, seed, variationLevel = 0.15) => {
  const varied = baseNumbers.map((num, index) => {
    const variation = seededRandom(seed + index * 10) * variationLevel;
    const direction = seededRandom(seed + index * 10 + 5) > 0.5 ? 1 : -1;
    const adjustedNum = num + (variation * direction * 10);
    
    // 1-45 범위 내로 제한
    return Math.max(1, Math.min(45, Math.round(adjustedNum)));
  });
  
  // 중복 제거 및 정렬
  const uniqueNumbers = [...new Set(varied)];
  
  // 부족한 번호는 시드 기반으로 생성
  while (uniqueNumbers.length < 6) {
    const seedIndex = uniqueNumbers.length * 7;
    const newNum = Math.floor(seededRandom(seed + seedIndex) * 45) + 1;
    if (!uniqueNumbers.includes(newNum)) {
      uniqueNumbers.push(newNum);
    }
  }
  
  return uniqueNumbers.slice(0, 6).sort((a, b) => a - b);
};

/**
 * 비동기 LSTM 시계열 예측 (UI 블로킹 방지 + 다양성 보장)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Promise<Object>} 예측 결과 객체
 */
export async function predictWithLSTM(historicalData) {
  try {
    // TensorFlow.js 초기화 확인
    if (!tfInitialized) {
      await initializeTensorFlow();
    }
    
    const dataHash = modelCache.generateDataHash(historicalData);
    const cacheKey = 'lstm_model';
    
    // 시간 기반 시드 생성 (예측 다양성 보장)
    const timeSeed = generateTimeSeed();
    const inputVariations = generateVariedInputs(historicalData, timeSeed);
    
    // 캐시된 모델 확인
    let model = modelCache.getModel(cacheKey, dataHash);
    let isFromCache = true;
    
    if (!model) {
      devLog('LSTM 모델 새로 훈련 시작...');
      isFromCache = false;
      
      // TensorFlow.js 백그라운드 설정 (추가 안전장치)
      await tf.ready();
      
      // 비동기적으로 데이터 준비 (다양한 입력 전략 사용)
      const prepareDataTask = () => {
        const selectedData = inputVariations.randomChoice === 0 ? inputVariations.recent :
                           inputVariations.randomChoice === 1 ? inputVariations.periodic :
                           inputVariations.sectional;
        
        const sequenceLength = 5;
        const sequences = prepareSequenceData(selectedData, sequenceLength);
        
        if (sequences.length < 10) {
          // 폴백: 전체 데이터 사용
          const fallbackSequences = prepareSequenceData(historicalData.slice(-100), sequenceLength);
          return normalizeData(fallbackSequences.slice(-30));
        }

        return normalizeData(sequences.slice(-30));
      };

      const { trainX, trainY } = await scheduleWork(prepareDataTask);

      // 모델 생성을 비동기적으로 처리
      const createModelTask = () => {
        return tf.sequential({
          layers: [
            tf.layers.lstm({ 
              units: 32,
              returnSequences: false,
              inputShape: [5, 6],
              kernelInitializer: 'glorotUniform', // Orthogonal 대신 사용
              recurrentInitializer: 'glorotUniform', // Orthogonal 대신 사용
              biasInitializer: 'zeros'
            }),
            tf.layers.dropout({ rate: 0.2 }),
            tf.layers.dense({ 
              units: 16,
              kernelInitializer: 'glorotUniform'
            }),
            tf.layers.dense({ 
              units: 6, 
              activation: 'sigmoid',
              kernelInitializer: 'glorotUniform'
            })
          ]
        });
      };

      model = await scheduleWork(createModelTask);

      // 컴파일
      await scheduleWork(() => {
        model.compile({
          optimizer: tf.train.adam(0.01),
          loss: 'meanSquaredError',
          metrics: ['mae']
        });
      });

      // 훈련을 여러 배치로 분할하여 UI 블로킹 방지
      devLog('LSTM 모델 훈련 중... (UI 블로킹 방지 모드)');
      
      const epochs = 10;
      for (let epoch = 0; epoch < epochs; epoch++) {
        await scheduleWork(async () => {
          await model.fit(trainX, trainY, {
            epochs: 1,
            batchSize: 4, // 배치 크기 줄임
            verbose: 0,
            validationSplit: 0.2
          });
        }, 3); // 높은 우선순위로 더 자주 yield
        
        // UI 업데이트를 위한 더 긴 지연
        await delay(20);
        
        // 중간 진행 상황 로깅
        if (epoch % 3 === 0) {
          devLog(`LSTM 훈련 진행: ${epoch + 1}/${epochs} epochs`);
        }
      }

      // 텐서 정리
      trainX.dispose();
      trainY.dispose();
      
      // 캐시에 저장
      modelCache.setModel(cacheKey, model, dataHash);
      devLog('LSTM 모델 훈련 및 캐시 저장 완료');
    } else {
      devLog('LSTM 모델 캐시에서 로드');
    }

    // 예측 실행을 비동기적으로 처리 (다양성 보장)
    const prediction = await scheduleWork(() => {
      // 매번 다른 시퀀스 길이와 데이터 범위 사용
      const sequenceVariation = Math.floor(seededRandom(timeSeed) * 3) + 4; // 4-6 시퀀스
      const selectedInputData = inputVariations.randomChoice === 0 ? inputVariations.recent :
                               inputVariations.randomChoice === 1 ? inputVariations.periodic :
                               inputVariations.sectional;
      
      const recentSequence = selectedInputData.slice(-sequenceVariation).map(d => 
        d.numbers.map(n => (n - 1) / 44)
      );
      
      // 시퀀스 길이가 다를 수 있으므로 패딩 또는 자르기
      const paddedSequence = recentSequence.slice(-5); // 최대 5개
      while (paddedSequence.length < 5) {
        paddedSequence.unshift(paddedSequence[0] || new Array(6).fill(0));
      }
      
      const inputTensor = tf.tensor3d([paddedSequence]);
      const predictionTensor = model.predict(inputTensor);
      
      // 즉시 데이터를 추출하고 텐서 정리
      const predictionData = predictionTensor.dataSync();
      inputTensor.dispose();
      predictionTensor.dispose();
      
      return predictionData;
    });

    // 예측 결과를 로또 번호로 변환 (랜덤성 주입)
    const basePredictedNumbers = Array.from(prediction)
      .map(val => Math.round(val * 44) + 1)
      .filter(num => num >= 1 && num <= 45)
      .slice(0, 6);

    // 시드 기반 랜덤성 주입으로 다양성 보장
    const numbers = injectRandomVariation(basePredictedNumbers, timeSeed, 0.12);

    // 현재 시간을 바탕으로 한 신뢰도 조정
    const confidenceVariation = seededRandom(timeSeed + 100) * 6 - 3; // ±3 범위
    const baseConfidence = isFromCache ? 88 : 85;
    const adjustedConfidence = Math.max(75, Math.min(95, Math.round(baseConfidence + confidenceVariation)));

    return {
      title: "AI 패턴 분석",
      description: "과거 데이터의 시간적 패턴을 학습한 지능형 예측",
      confidence: adjustedConfidence,
      numbers: numbers,
      reasons: [
        isFromCache ? "학습한 모델 캐시로 빠른 예측 실행" : "고급 AI 모델 훈련으로 정확도 향상",
        `시간 기반 다양성 보장으로 매번 다른 결과`,
        `최적화된 데이터 분석 전략 적용`,
        "고급 패턴 인식을 통한 예측"
      ],
      summary: `AI가 과거 당첨 패턴을 깊이 학습하여 생성한 예측 결과입니다.${isFromCache ? ' (빠른 실행)' : ' (고급 훈련)'}`
    };

  } catch (error) {
    devError('LSTM 예측 오류:', error);
    return fallbackPrediction("AI 패턴 분석", historicalData);
  }
}

/**
 * 비동기 Transformer 기반 시퀀스 예측 (UI 블로킹 방지 + 다양성 보장)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Promise<Object>} 예측 결과 객체
 */
export async function predictWithTransformer(historicalData) {
  try {
    // TensorFlow.js 초기화 확인
    if (!tfInitialized) {
      await initializeTensorFlow();
    }
    
    const dataHash = modelCache.generateDataHash(historicalData);
    const cacheKey = 'transformer_model';
    
    // 시간 기반 시드 생성 (LSTM과 다른 시드)
    const timeSeed = generateTimeSeed() + 1000;
    const inputVariations = generateVariedInputs(historicalData, timeSeed);
    
    // 캐시된 모델 확인
    let model = modelCache.getModel(cacheKey, dataHash);
    let isFromCache = true;
    
    if (!model) {
      devLog('Transformer 모델 새로 훈련 시작...');
      isFromCache = false;
      
      // 비동기적으로 데이터 준비
      const prepareDataTask = () => {
        // Transformer는 더 다양한 데이터 전략 사용
        const combinedData = [
          ...inputVariations.recent.slice(-20),
          ...inputVariations.periodic.slice(-15),
          ...inputVariations.sectional.slice(-25)
        ];
        
        const uniqueData = combinedData.filter((item, index, self) => 
          index === self.findIndex(t => t.round === item.round)
        );
        
        const sequenceLength = 4;
        const sequences = prepareSequenceData(uniqueData, sequenceLength);
        
        if (sequences.length < 8) {
          const fallbackSequences = prepareSequenceData(historicalData.slice(-80), sequenceLength);
          return normalizeData(fallbackSequences.slice(-20));
        }

        return normalizeData(sequences.slice(-20));
      };

      const { trainX, trainY } = await scheduleWork(prepareDataTask);

      // 모델 생성을 비동기적으로 처리
      const createModelTask = () => {
        return tf.sequential({
          layers: [
            tf.layers.dense({ 
              units: 24, 
              activation: 'relu', 
              inputShape: [4 * 6],
              kernelInitializer: 'glorotUniform'
            }),
            tf.layers.dropout({ rate: 0.3 }),
            tf.layers.dense({ 
              units: 12, 
              activation: 'relu',
              kernelInitializer: 'glorotUniform'
            }),
            tf.layers.dense({ 
              units: 6, 
              activation: 'sigmoid',
              kernelInitializer: 'glorotUniform'
            })
          ]
        });
      };

      model = await scheduleWork(createModelTask);

      // 입력 데이터 형태 변경
      const flattenedX = await scheduleWork(() => trainX.reshape([trainX.shape[0], -1]));

      await scheduleWork(() => {
        model.compile({
          optimizer: tf.train.adam(0.015),
          loss: 'meanSquaredError'
        });
      });

      // 훈련을 여러 배치로 분할
      devLog('Transformer 모델 훈련 중... (UI 블로킹 방지 모드)');
      
      const epochs = 8;
      for (let epoch = 0; epoch < epochs; epoch++) {
        await scheduleWork(async () => {
          await model.fit(flattenedX, trainY, {
            epochs: 1,
            batchSize: 2, // 배치 크기 더 줄임
            verbose: 0
          });
        }, 3); // 높은 우선순위로 더 자주 yield
        
        // UI 업데이트를 위한 더 긴 지연
        await delay(25);
        
        // 중간 진행 상황 로깅
        if (epoch % 2 === 0) {
          devLog(`Transformer 훈련 진행: ${epoch + 1}/${epochs} epochs`);
        }
      }

      // 텐서 정리
      trainX.dispose();
      trainY.dispose();
      flattenedX.dispose();
      
      // 캐시에 저장
      modelCache.setModel(cacheKey, model, dataHash);
      devLog('Transformer 모델 훈련 및 캐시 저장 완료');
    } else {
      devLog('Transformer 모델 캐시에서 로드');
    }

    // 예측 실행을 비동기적으로 처리 (다양성 보장)
    const prediction = await scheduleWork(() => {
      // 매번 다른 입력 조합 사용
      const strategy = Math.floor(seededRandom(timeSeed) * 3);
      let selectedData;
      
      switch(strategy) {
        case 0:
          selectedData = inputVariations.recent.slice(-4);
          break;
        case 1:
          selectedData = inputVariations.periodic.slice(-4);
          break;
        default:
          selectedData = inputVariations.sectional.slice(-4);
      }
      
      // 부족한 데이터 패딩
      while (selectedData.length < 4) {
        selectedData.unshift(selectedData[0] || { numbers: [1,2,3,4,5,6] });
      }
      
      const recentSequence = selectedData.map(d => d.numbers.map(n => (n - 1) / 44));
      const flatInput = tf.tensor2d([recentSequence.flat()]);
      const predictionTensor = model.predict(flatInput);
      
      const predictionData = predictionTensor.dataSync();
      flatInput.dispose();
      predictionTensor.dispose();
      
      return predictionData;
    });

    // 예측 결과를 로또 번호로 변환 (랜덤성 주입)
    const basePredictedNumbers = Array.from(prediction)
      .map(val => Math.round(val * 44) + 1)
      .filter(num => num >= 1 && num <= 45)
      .slice(0, 6);

    // Transformer는 더 높은 변동성 적용
    const numbers = injectRandomVariation(basePredictedNumbers, timeSeed, 0.18);

    // 시간 기반 신뢰도 조정
    const confidenceVariation = seededRandom(timeSeed + 200) * 8 - 4; // ±4 범위
    const baseConfidence = isFromCache ? 84 : 81;
    const adjustedConfidence = Math.max(70, Math.min(92, Math.round(baseConfidence + confidenceVariation)));

    return {
      title: "AI 시퀀스 분석",
      description: "다양한 관점에서 번호 조합을 분석하는 지능형 모델",
      confidence: adjustedConfidence,
      numbers: numbers,
      reasons: [
        isFromCache ? "학습한 모델 캐시로 즉시 예측 실행" : "고급 AI 모델 훈련으로 정확도 향상",
        `시간 기반 다양성 보장으로 매번 다른 결과`,
        "다중 전략 데이터 조합으로 패턴 강화",
        "고급 분석 기법을 통한 최적화된 예측"
      ],
      summary: `AI가 다양한 관점에서 번호 패턴을 분석하여 매번 새로운 조합을 생성합니다.${isFromCache ? ' (빠른 실행)' : ' (고급 훈련)'}`
    };

  } catch (error) {
    devError('Transformer 예측 오류:', error);
    return fallbackPrediction("AI 시퀀스 분석", historicalData);
  }
}

/**
 * ml-matrix 기반 GAN 생성 모델 예측 (캐시 적용 + 다양성 보장)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Promise<Object>} 예측 결과 객체
 */
export async function predictWithGAN(historicalData) {
  try {
    // TensorFlow.js 초기화 확인
    if (!tfInitialized) {
      await initializeTensorFlow();
    }
    
    const dataHash = modelCache.generateDataHash(historicalData);
    const cacheKey = 'gan_analysis';
    
    // 시간 기반 시드 생성 (GAN 전용)
    const timeSeed = generateTimeSeed() + 2000;
    
    // 캐시된 분석 결과 확인
    let analysisResult = modelCache.getModel(cacheKey, dataHash);
    let isFromCache = true;
    
    if (!analysisResult) {
      devLog('GAN 분석 새로 실행 시작...');
      isFromCache = false;
      
      // 비동기적으로 분석 수행
      const analysisTask = () => {
        const realDistribution = analyzeNumberDistribution(historicalData);
        
        // 시드 기반으로 다양한 후보 생성
        const candidateCount = 80; // 기본 50에서 증가
        const generator = [];
        
        for (let i = 0; i < candidateCount; i++) {
          const seedOffset = timeSeed + i * 13;
          const candidate = [];
          
          for (let j = 0; j < 6; j++) {
            const randomValue = seededRandom(seedOffset + j * 7);
            const weightedNumber = Math.floor(randomValue * 45) + 1;
            
            if (!candidate.includes(weightedNumber)) {
              candidate.push(weightedNumber);
            } else {
              // 중복 시 시드 기반 대체 번호 생성
              let alternative = Math.floor(seededRandom(seedOffset + j * 7 + 100) * 45) + 1;
              while (candidate.includes(alternative)) {
                alternative = (alternative % 45) + 1;
              }
              candidate.push(alternative);
            }
          }
          
          generator.push(candidate.sort((a, b) => a - b));
        }
        
        const discriminatedResults = generator.map(combination => {
          const similarity = calculateDistributionSimilarity(combination, realDistribution);
          // 시간 기반 다양성 보너스 추가
          const diversityBonus = seededRandom(timeSeed + combination[0]) * 0.1;
          return { combination, similarity: similarity + diversityBonus };
        }).sort((a, b) => b.similarity - a.similarity);

        return {
          topCandidates: discriminatedResults.slice(0, 8), // 5에서 8로 증가
          realDistribution,
          generationSeed: timeSeed
        };
      };

      analysisResult = await scheduleWork(analysisTask);
      
      // 캐시에 저장
      modelCache.setModel(cacheKey, analysisResult, dataHash);
      devLog('GAN 분석 완료 및 캐시 저장');
    } else {
      devLog('GAN 분석 결과 캐시에서 로드');
    }

    // 시간 기반으로 상위 후보 중에서 동적 선택
    const selectionRange = Math.min(5, analysisResult.topCandidates.length);
    const selectionIndex = Math.floor(seededRandom(timeSeed + 50) * selectionRange);
    const selectedCandidate = analysisResult.topCandidates[selectionIndex];
    
    // 선택된 후보에 미세한 변동 적용
    const finalNumbers = injectRandomVariation(selectedCandidate.combination, timeSeed, 0.08);


    // 동적 신뢰도 계산
    const similarityScore = selectedCandidate.similarity * 100;
    const selectionBonus = (5 - selectionIndex) * 2; // 상위 선택일수록 보너스
    const timeVariation = seededRandom(timeSeed + 300) * 6 - 3; // ±3 범위
    const baseConfidence = isFromCache ? 79 : 76;
    const adjustedConfidence = Math.max(65, Math.min(90, 
      Math.round(baseConfidence + selectionBonus + timeVariation)
    ));

    return {
      title: "AI 생성 모델",
      description: "확률 분포를 학습하여 최적화된 번호 조합을 생성",
      confidence: adjustedConfidence,
      numbers: finalNumbers,
      reasons: [
        isFromCache ? "학습한 모델 캐시로 즉시 결과 생성" : "고급 AI 모델 분석으로 정확도 향상",
        `시간 기반 다양성 보장으로 매번 다른 결과`,
        `상위 ${selectionIndex + 1}번째 후보 선택 (적합도: ${similarityScore.toFixed(1)}%)`,
        "생성형 AI를 통한 최적화된 번호 조합"
      ],
      summary: `AI가 실제 당첨 패턴을 학습하여 유사하면서도 매번 다른 번호 조합을 생성합니다.${isFromCache ? ' (빠른 실행)' : ' (고급 분석)'}`
    };

  } catch (error) {
    devError('GAN 예측 오류:', error);
    return fallbackPrediction("AI 생성 모델", historicalData);
  }
}

/**
 * 비동기 CNN 기반 컨볼루션 분석 (캐시 적용 + 다양성 보장)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Promise<Object>} 예측 결과 객체
 */
export async function predictWithCNN(historicalData) {
  try {
    // TensorFlow.js 초기화 확인
    if (!tfInitialized) {
      await initializeTensorFlow();
    }
    
    const dataHash = modelCache.generateDataHash(historicalData);
    const cacheKey = 'cnn_conv_analysis';
    
    // 시간 기반 시드 생성 (CNN 전용)
    const timeSeed = generateTimeSeed() + 3000;
    
    // 캐시된 분석 결과 확인
    let analysisResult = modelCache.getModel(cacheKey, dataHash);
    let isFromCache = true;
    
    if (!analysisResult) {
      devLog('CNN 컨볼루션 분석 새로 실행...');
      isFromCache = false;
      
      // 비동기적으로 CNN 분석 수행
      const analysisTask = () => {
        // 시퀀스 데이터 준비 (최근 10회차 패턴 분석)
        const sequenceLength = 10;
        const sequences = [];
        for (let i = sequenceLength; i < historicalData.length; i++) {
          const sequence = [];
          for (let j = i - sequenceLength; j < i; j++) {
            sequence.push(historicalData[j].numbers);
          }
          sequences.push(sequence.flat());
        }
        
        // 1D 컨볼루션 시뮬레이션 (패턴 감지)
        const kernelSize = 3;
        const filters = 32;
        const convResults = [];
        
        sequences.forEach((seq, seqIdx) => {
          const convOutput = [];
          
          // 여러 필터로 컨볼루션 연산 시뮬레이션
          for (let f = 0; f < filters; f++) {
            const filterResult = [];
            for (let i = 0; i <= seq.length - kernelSize; i++) {
              let sum = 0;
              for (let j = 0; j < kernelSize; j++) {
                // 시드 기반 가중치 생성
                const weight = seededRandom(timeSeed + f * 100 + j);
                sum += seq[i + j] * weight;
              }
              filterResult.push(Math.max(0, sum)); // ReLU 활성화
            }
            convOutput.push(filterResult);
          }
          
          convResults.push(convOutput);
        });
        
        // 전역 최대 풀링 및 특징 추출
        const globalFeatures = convResults[convResults.length - 1];
        const featureMap = globalFeatures.map(filter => 
          Math.max(...filter.map(v => Math.abs(v)))
        );
        
        return {
          convolutionResults: convResults,
          featureMap: featureMap,
          analysisTimestamp: Date.now(),
          generationSeed: timeSeed
        };
      };

      analysisResult = await scheduleWork(analysisTask);
      
      // 캐시에 저장
      modelCache.setModel(cacheKey, analysisResult, dataHash);
      devLog('CNN 컨볼루션 분석 완료 및 캐시 저장');
    } else {
      devLog('CNN 분석 결과 캐시에서 로드');
    }

    // 특징 맵을 기반으로 번호 생성
    const numbers = [];
    const featureMap = analysisResult.featureMap;
    
    // 상위 특징들을 번호로 변환
    const topFeatures = featureMap
      .map((value, index) => ({ value, index }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // 상위 8개 특징
    
    for (let i = 0; i < 6; i++) {
      const featureIndex = i % topFeatures.length;
      const baseValue = topFeatures[featureIndex].value;
      
      // 특징값을 1-45 범위로 변환
      let number = Math.floor((baseValue * 44) % 45) + 1;
      
      // 시간 기반 변동 추가
      const variation = seededRandom(timeSeed + i * 17);
      number = Math.floor(number + (variation - 0.5) * 10);
      number = Math.max(1, Math.min(45, number));
      
      // 중복 제거
      while (numbers.includes(number)) {
        number = (number % 45) + 1;
      }
      numbers.push(number);
    }
    
    // 동적 신뢰도 계산
    const featureStrength = featureMap.reduce((sum, val) => sum + val, 0) / featureMap.length;
    const convComplexity = Math.min(analysisResult.convolutionResults.length / 100, 1) * 5;
    const timeVariation = seededRandom(timeSeed + 400) * 8 - 4; // ±4 범위
    const baseConfidence = isFromCache ? 83 : 80;
    const adjustedConfidence = Math.max(70, Math.min(92, 
      Math.round(baseConfidence + convComplexity + timeVariation)
    ));

    return {
      title: "AI 컨볼루션 분석",
      description: "국소 패턴을 감지하여 연속된 번호 특성을 학습한 분석",
      confidence: adjustedConfidence,
      numbers: numbers.sort((a, b) => a - b),
      reasons: [
        isFromCache ? "학습한 모델 캐시로 즉시 패턴 인식" : "고급 AI 모델 분석으로 정확도 향상",
        `시간 기반 다양성 보장으로 매번 다른 결과`,
        `${topFeatures.length}개 핵심 특징으로 번호 생성`,
        "컨볼루션 필터를 통한 지역 패턴 감지"
      ],
      summary: `AI가 번호 시퀀스의 국소 패턴을 학습하여 연속성을 고려한 번호를 생성합니다.${isFromCache ? ' (빠른 실행)' : ' (고급 분석)'}`
    };

  } catch (error) {
    devError('CNN 예측 오류:', error);
    return fallbackPrediction("AI 컨볼루션 분석", historicalData);
  }
}

/**
 * 비동기 VAE 기반 잠재공간 분석 (캐시 적용 + 다양성 보장)
 * @param {Array} historicalData - 과거 데이터
 * @returns {Promise<Object>} 예측 결과 객체
 */
export async function predictWithVAE(historicalData) {
  try {
    // TensorFlow.js 초기화 확인
    if (!tfInitialized) {
      await initializeTensorFlow();
    }
    
    const dataHash = modelCache.generateDataHash(historicalData);
    const cacheKey = 'vae_latent_analysis';
    
    // 시간 기반 시드 생성 (VAE 전용)
    const timeSeed = generateTimeSeed() + 4000;
    
    // 캐시된 분석 결과 확인
    let analysisResult = modelCache.getModel(cacheKey, dataHash);
    let isFromCache = true;
    
    if (!analysisResult) {
      devLog('VAE 잠재공간 분석 새로 실행...');
      isFromCache = false;
      
      // 비동기적으로 VAE 분석 수행
      const analysisTask = () => {
        // 데이터를 잠재 공간으로 인코딩 시뮬레이션
        const latentDim = 16;
        const encoderResults = [];
        
        historicalData.slice(-50).forEach((draw, idx) => {
          const input = draw.numbers.map(n => n / 45.0); // 정규화
          
          // 인코더: 평균과 분산 계산
          const mean = [];
          const logvar = [];
          
          for (let i = 0; i < latentDim; i++) {
            // 시드 기반 가중치로 잠재 변수 계산
            const meanWeight = seededRandom(timeSeed + idx * 100 + i);
            const varWeight = seededRandom(timeSeed + idx * 200 + i);
            
            let meanSum = 0, varSum = 0;
            input.forEach((val, j) => {
              meanSum += val * seededRandom(timeSeed + i * 10 + j);
              varSum += val * seededRandom(timeSeed + i * 20 + j + 1000);
            });
            
            mean.push(meanSum / input.length);
            logvar.push(Math.log(Math.abs(varSum / input.length) + 1e-8));
          }
          
          encoderResults.push({ mean, logvar, originalNumbers: draw.numbers });
        });
        
        // 잠재 공간에서 디코딩
        const decodedResults = [];
        encoderResults.forEach((encoded, idx) => {
          const decoded = [];
          
          // 재매개화 트릭 (reparameterization trick)
          for (let i = 0; i < latentDim; i++) {
            const eps = seededRandom(timeSeed + idx * 300 + i) * 2 - 1; // 표준 정규분포 근사
            const sample = encoded.mean[i] + Math.exp(encoded.logvar[i] * 0.5) * eps;
            decoded.push(sample);
          }
          
          decodedResults.push(decoded);
        });
        
        return {
          encoderResults: encoderResults,
          decodedResults: decodedResults,
          latentDimension: latentDim,
          analysisTimestamp: Date.now(),
          generationSeed: timeSeed
        };
      };

      analysisResult = await scheduleWork(analysisTask);
      
      // 캐시에 저장
      modelCache.setModel(cacheKey, analysisResult, dataHash);
      devLog('VAE 잠재공간 분석 완료 및 캐시 저장');
    } else {
      devLog('VAE 분석 결과 캐시에서 로드');
    }

    // 잠재 공간에서 새로운 샘플 생성
    const latentSample = [];
    for (let i = 0; i < analysisResult.latentDimension; i++) {
      // 과거 잠재 변수들의 평균과 분산으로 새로운 샘플 생성
      const pastValues = analysisResult.decodedResults.map(result => result[i]);
      const mean = pastValues.reduce((sum, val) => sum + val, 0) / pastValues.length;
      const variance = pastValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / pastValues.length;
      
      const eps = seededRandom(timeSeed + i * 500) * 2 - 1;
      const sample = mean + Math.sqrt(variance) * eps;
      latentSample.push(sample);
    }
    
    // 디코더: 잠재 변수를 번호로 변환
    const numbers = [];
    for (let i = 0; i < 6; i++) {
      // 여러 잠재 변수를 조합하여 번호 생성
      let value = 0;
      for (let j = 0; j < analysisResult.latentDimension; j++) {
        const weight = seededRandom(timeSeed + i * 50 + j);
        value += latentSample[j] * weight;
      }
      
      // 값을 1-45 범위로 변환
      let number = Math.floor(Math.abs(value * 45)) % 45 + 1;
      
      // 중복 제거
      while (numbers.includes(number)) {
        number = (number % 45) + 1;
      }
      numbers.push(number);
    }
    
    // 동적 신뢰도 계산
    const latentComplexity = analysisResult.latentDimension / 16 * 5; // 잠재 차원 복잡도
    const reconstructionQuality = Math.min(analysisResult.decodedResults.length / 50, 1) * 3;
    const timeVariation = seededRandom(timeSeed + 500) * 6 - 3; // ±3 범위
    const baseConfidence = isFromCache ? 82 : 79;
    const adjustedConfidence = Math.max(68, Math.min(91, 
      Math.round(baseConfidence + latentComplexity + reconstructionQuality + timeVariation)
    ));

    return {
      title: "AI 잠재공간 분석",
      description: "압축된 특징 공간에서 확률적 생성을 통한 번호 조합",
      confidence: adjustedConfidence,
      numbers: numbers.sort((a, b) => a - b),
      reasons: [
        isFromCache ? "학습한 모델 캐시로 즉시 생성 실행" : "고급 AI 모델 분석으로 정확도 향상",
        `시간 기반 다양성 보장으로 매번 다른 결과`,
        `${analysisResult.latentDimension}차원 잠재공간에서 특징 추출`,
        "확률적 생성을 통한 창의적 번호 조합"
      ],
      summary: `AI가 번호 패턴을 압축된 잠재공간에서 학습하고 확률적으로 새로운 조합을 생성합니다.${isFromCache ? ' (빠른 실행)' : ' (고급 분석)'}`
    };

  } catch (error) {
    devError('VAE 예측 오류:', error);
    return fallbackPrediction("AI 잠재공간 분석", historicalData);
  }
}

/**
 * 고급 AI 예측 함수 실행 (UI 블로킹 방지)
 * @param {Array} historicalData - 과거 데이터 (선택적, 없으면 자동 로드)
 * @returns {Promise<Array>} 예측 결과 배열
 */
/**
 * 진행률 콜백과 함께 고급 AI 예측 실행
 * @param {Array} historicalData - 과거 데이터
 * @param {Function} progressCallback - 진행률 콜백 함수 (step, percent) => void
 * @returns {Promise<Array>} 예측 결과 배열
 */
export async function runAdvancedAIPredictionsWithProgress(historicalData = null, progressCallback) {
  try {
    // 데이터가 없으면 자동으로 로드
    if (!historicalData) {
      progressCallback('로또 데이터 자동 로딩...', 5);
      devLog('로또 데이터 자동 로딩...');
      historicalData = await loadLottoData();
      devLog(`로또 데이터 로드 완료: ${historicalData.length}개 회차`);
    }
    
    // TensorFlow.js 초기화
    progressCallback('TensorFlow.js 초기화 중...', 10);
    await tf.ready();
    
    const startTime = Date.now();
    devLog('고급 AI 예측 시작... (진행률 모니터링 모드)');
    
    // 캐시 정보 확인
    const cacheInfo = modelCache.getCacheInfo();
    devLog('캐시 상태:', cacheInfo);
    
    // TensorFlow.js 백그라운드 실행 모드 설정
    if (tf.env && tf.env().getBool) {
      try {
        tf.env().set('WEBGL_CPU_FORWARD', false);
        tf.env().set('WEBGL_PACK', true);
      } catch (error) {
        devLog('TensorFlow.js 환경 설정 건너뜀:', error.message);
      }
    }

    // 예측을 순차적으로 실행하되, 각각 진행률 보고
    const predictions = [];
    const models = [
      { name: 'AI 패턴 분석', fn: predictWithLSTM },
      { name: 'AI 시퀀스 분석', fn: predictWithTransformer },
      { name: 'AI 생성 모델', fn: predictWithGAN },
      { name: 'AI 컨볼루션 분석', fn: predictWithCNN },
      { name: 'AI 잠재공간 분석', fn: predictWithVAE }
    ];
    
    for (let i = 0; i < models.length; i++) {
      const model = models[i];
      const baseProgress = 15 + (i * 15); // 15%, 30%, 45%, 60%, 75%
      
      progressCallback(`${model.name} 모델 실행 중...`, baseProgress);
      const prediction = await model.fn(historicalData);
      predictions.push(prediction);
      
      const completedProgress = baseProgress + 12; // 27%, 42%, 57%, 72%, 87%
      progressCallback(`${model.name} 모델 완료`, completedProgress);
      
      await delay(50); // UI 업데이트 시간 확보
    }

    const endTime = Date.now();
    const executionTime = (endTime - startTime) / 1000;
    devLog(`AI 예측 완료 (${executionTime.toFixed(2)}초): ${predictions.length}개 결과`);
    
    progressCallback('고급 AI 예측 완료', 90);
    return predictions;
    
  } catch (error) {
    devError('고급 AI 예측 실행 오류:', error);
    progressCallback('오류 발생 - 폴백 모드 실행', 80);
    
    // 폴백: 기존 예측 함수들 사용
    return [
      {
        ...fallbackPrediction("AI 패턴 분석", historicalData),
        title: "AI 패턴 분석",
        description: "데이터 패턴을 학습한 AI 분석"
      },
      {
        ...fallbackPrediction("AI 시퀀스 분석", historicalData),
        title: "AI 시퀀스 분석",
        description: "다양한 관점에서 번호 조합을 분석"
      },
      {
        ...fallbackPrediction("AI 생성 모델", historicalData),
        title: "AI 생성 모델",
        description: "확률 분포를 학습한 생성형 AI"
      },
      {
        ...fallbackPrediction("AI 컨볼루션 분석", historicalData),
        title: "AI 컨볼루션 분석",
        description: "국소 패턴을 감지한 컨볼루션 분석"
      },
      {
        ...fallbackPrediction("AI 잠재공간 분석", historicalData),
        title: "AI 잠재공간 분석",
        description: "압축된 특징 공간에서의 확률적 생성"
      }
    ];
  }
}

export async function runAdvancedAIPredictions(historicalData = null) {
  try {
    // 데이터가 없으면 자동으로 로드
    if (!historicalData) {
      devLog('로또 데이터 자동 로딩...');
      historicalData = await loadLottoData();
      devLog(`로또 데이터 로드 완료: ${historicalData.length}개 회차`);
    }
    
    // TensorFlow.js 초기화
    await tf.ready();
    
    const startTime = Date.now();
    devLog('고급 AI 예측 시작... (UI 블로킹 방지 모드)');
    
    // 캐시 정보 확인
    const cacheInfo = modelCache.getCacheInfo();
    devLog('캐시 상태:', cacheInfo);
    
    // TensorFlow.js 백그라운드 실행 모드 설정 (지원되는 경우)
    if (tf.env && tf.env().getBool) {
      try {
        tf.env().set('WEBGL_CPU_FORWARD', false);
        tf.env().set('WEBGL_PACK', true);
      } catch (error) {
        devLog('TensorFlow.js 환경 설정 건너뜀:', error.message);
      }
    }

    // 예측을 순차적으로 실행하되, 각각 비동기 처리
    const predictions = [];
    
    // 1. AI 패턴 분석
    const lstm = await predictWithLSTM(historicalData);
    predictions.push(lstm);
    await delay(50); // UI 업데이트 시간 확보
    
    // 2. AI 시퀀스 분석
    const transformer = await predictWithTransformer(historicalData);
    predictions.push(transformer);
    await delay(50); // UI 업데이트 시간 확보
    
    // 3. AI 생성 모델
    const gan = await predictWithGAN(historicalData);
    predictions.push(gan);
    await delay(50); // UI 업데이트 시간 확보
    
    // 4. AI 컨볼루션 분석
    const cnn = await predictWithCNN(historicalData);
    predictions.push(cnn);
    await delay(50); // UI 업데이트 시간 확보
    
    // 5. AI 잠재공간 분석
    const vae = await predictWithVAE(historicalData);
    predictions.push(vae);

    const endTime = Date.now();
    const executionTime = (endTime - startTime) / 1000;
    devLog(`AI 예측 완료 (${executionTime.toFixed(2)}초): ${predictions.length}개 결과`);
    
    return predictions;
    
  } catch (error) {
    devError('고급 AI 예측 실행 오류:', error);
    // 폴백: 기존 예측 함수들 사용
    return [
      {
        ...fallbackPrediction("AI 패턴 분석", historicalData),
        title: "AI 패턴 분석",
        description: "데이터 패턴을 학습한 AI 분석"
      },
      {
        ...fallbackPrediction("AI 시퀀스 분석", historicalData),
        title: "AI 시퀀스 분석",
        description: "다양한 관점에서 번호 조합을 분석"
      },
      {
        ...fallbackPrediction("AI 생성 모델", historicalData),
        title: "AI 생성 모델",
        description: "확률 분포를 학습한 생성형 AI"
      },
      {
        ...fallbackPrediction("AI 컨볼루션 분석", historicalData),
        title: "AI 컨볼루션 분석",
        description: "국소 패턴을 감지한 컨볼루션 분석"
      },
      {
        ...fallbackPrediction("AI 잠재공간 분석", historicalData),
        title: "AI 잠재공간 분석",
        description: "압축된 특징 공간에서의 확률적 생성"
      }
    ];
  }
}

/**
 * 캐시 정보 조회 (디버깅용)
 * @returns {Object} 캐시 상태 정보
 */
export function getModelCacheInfo() {
  return modelCache.getCacheInfo();
}

/**
 * 캐시 정보 조회 (AIPrediction.vue 호환성)
 * @returns {Object} 캐시 상태 정보
 */
export function getCacheInfo() {
  return modelCache.getCacheInfo();
}

/**
 * 캐시 강제 초기화 (필요 시 사용)
 */
export function clearModelCache() {
  modelCache.clearCache();
  devLog('학습한 모델 캐시가 초기화되었습니다.');
}

// ============= 보조 함수들 =============

/**
 * 시퀀스 데이터 준비
 */
function prepareSequenceData(data, sequenceLength) {
  const sequences = [];
  for (let i = sequenceLength; i < data.length; i++) {
    const sequence = data.slice(i - sequenceLength, i).map(d => d.numbers);
    const target = data[i].numbers;
    sequences.push({ sequence, target });
  }
  return sequences;
}

/**
 * 데이터 정규화
 */
function normalizeData(sequences) {
  const seqData = sequences.map(s => s.sequence.flat().map(n => (n - 1) / 44));
  const targetData = sequences.map(s => s.target.map(n => (n - 1) / 44));
  
  const trainX = tf.tensor3d(seqData.map(seq => {
    const reshaped = [];
    for (let i = 0; i < seq.length; i += 6) {
      reshaped.push(seq.slice(i, i + 6));
    }
    return reshaped;
  }));
  
  const trainY = tf.tensor2d(targetData);
  
  return { trainX, trainY };
}

/**
 * 번호 분포 분석
 */
function analyzeNumberDistribution(data) {
  const stats = calculateStats(data);
  return {
    frequency: stats.frequency,
    oddEvenRatio: stats.oddPercentage / 100,
    lowHighRatio: stats.lowPercentage / 100,
    averageSum: stats.averageSum,
    consecutiveRate: stats.consecutivePercentage / 100
  };
}

/**
 * 후보 조합 생성
 */
function generateCandidateCombinations(count) {
  const combinations = [];
  for (let i = 0; i < count; i++) {
    combinations.push(generateRandomLottoNumbers());
  }
  return combinations;
}

/**
 * 분포 유사도 계산
 */
function calculateDistributionSimilarity(combination, targetDistribution) {
  const oddCount = combination.filter(n => n % 2 === 1).length;
  const lowCount = combination.filter(n => n <= 22).length;
  const sum = combination.reduce((a, b) => a + b, 0);
  
  const oddRatio = oddCount / 6;
  const lowRatio = lowCount / 6;
  
  const oddSimilarity = 1 - Math.abs(oddRatio - targetDistribution.oddEvenRatio);
  const lowSimilarity = 1 - Math.abs(lowRatio - targetDistribution.lowHighRatio);
  const sumSimilarity = 1 - Math.abs(sum - targetDistribution.averageSum) / targetDistribution.averageSum;
  
  return (oddSimilarity + lowSimilarity + sumSimilarity) / 3;
}

/**
 * 폴백 예측 (오류 시 사용)
 */
function fallbackPrediction(title, historicalData) {
  const numbers = predictWithDecisionTree(historicalData);
  const confidence = Math.floor(Math.random() * 20) + 65; // 65-85%
  
  return {
    title,
    confidence,
    numbers,
    reasons: [
      "통계적 빈도 분석 기반 예측",
      "과거 패턴의 가중치 적용",
      "확률적 최적화 알고리즘 사용"
    ],
    summary: "통계적 분석과 머신러닝 기법을 결합한 예측 결과입니다."
  };
}

/**
 * 진행률 콜백과 함께 5게임 AI 번호 추천
 * @param {Function} progressCallback - 진행률 콜백 함수 (step, percent) => void
 * @returns {Promise<Array>} 5게임의 번호 조합 배열
 */
export async function generateRecommendedGamesWithProgress(progressCallback) {
  try {
    // 캐시 상태 확인
    const cacheInfo = getCacheInfo();
    const hasCache = cacheInfo.hasCache;
    
    progressCallback("로또 데이터 로딩 중...", 5);
    const realData = await loadLottoData()
    const games = []
    
    devLog('🎯 AI 번호 추천 시작...')
    
    // === 5개 고급 AI 모델 실행 ===
    if (hasCache) {
      progressCallback("학습한 AI 모델 캐시 로딩 중...", 15);
      devLog('📋 캐시된 모델 사용 - 빠른 실행');
      // 캐시된 경우 빠르게 실행
      await delay(200); // 최소 시뮬레이션
    } else {
      progressCallback("고급 AI 모델 훈련 중...", 20);
      devLog('🧠 AI 모델 새로 훈련 - 시간 소요');
    }
    
    const advancedAIResults = await runAdvancedAIPredictionsWithProgress(realData, (step, percent) => {
      const baseProgress = hasCache ? 15 : 20;
      const maxProgress = 90;
      const currentProgress = baseProgress + (percent * (maxProgress - baseProgress) / 100);
      progressCallback(step, currentProgress);
    });
    
    // 고급 AI 결과를 게임 형식으로 변환 (5게임 모두)
    advancedAIResults.forEach((result, index) => {
      games.push({
        gameNumber: index + 1,
        numbers: result.numbers.sort((a, b) => a - b),
        description: result.description,
        confidence: result.confidence,
        strategy: result.title,
        type: 'advanced',
        details: result.summary
      })
    })
    
    progressCallback("추천 완료!", 95);
    
    devLog(`✅ 총 ${games.length}게임 생성 완료`)
    devLog(`🎯 고급 AI 모델: ${games.length}게임`)
    devLog(`🚀 캐시 사용: ${hasCache ? 'Yes' : 'No'}`)
    
    return games
    
  } catch (error) {
    devError('게임 생성 오류:', error)
    progressCallback("오류 발생 - 폴백 모드", 95);
    // 폴백: 기존 방식 사용
    return generateFallbackGames()
  }
}

/**
 * 5게임 AI 번호 추천 (기존 함수 - 호환성 유지)
 * @returns {Promise<Array>} 5게임의 번호 조합 배열
 */
export async function generateRecommendedGames() {
  try {
    const realData = await loadLottoData()
    const games = []
    
    devLog('🎯 AI 번호 추천 시작...')
    
    // === 5개 고급 AI 모델 실행 ===
    devLog('🎯 고급 AI 모델 실행 중...')
    const advancedAIResults = await runAdvancedAIPredictions(realData)
    
    // 고급 AI 결과를 게임 형식으로 변환 (5게임 모두)
    advancedAIResults.forEach((result, index) => {
      games.push({
        gameNumber: index + 1,
        numbers: result.numbers.sort((a, b) => a - b),
        description: result.description,
        confidence: result.confidence,
        strategy: result.title,
        type: 'advanced',
        details: result.summary
      })
    })
    
    devLog(`✅ 총 ${games.length}게임 생성 완료`)
    devLog(`🎯 고급 AI 모델: ${games.length}게임`)
    
    return games
    
  } catch (error) {
    devError('게임 생성 오류:', error)
    // 폴백: 기존 방식 사용
    return generateFallbackGames()
  }
}

/**
 * 폴백 게임 생성 (오류 시 사용)
 */
function generateFallbackGames() {
  const games = []
  const strategies = [
    { name: "패턴 분석", desc: "과거 당첨 패턴을 분석한 체계적인 번호 조합" },
    { name: "균형 분석", desc: "다양한 분석 결과를 종합한 균형 잡힌 조합" },
    { name: "고급 예측", desc: "복잡한 데이터 관계를 학습한 고급 예측" },
    { name: "확률 최적화", desc: "출현 빈도와 확률을 고려한 최적화된 조합" },
    { name: "창의적 접근", desc: "시간 변수를 반영한 독창적인 번호 조합" }
  ]
  
  for (let i = 0; i < 5; i++) {
    const numbers = generateRandomLottoNumbers()
    games.push({
      gameNumber: i + 1,
      numbers: numbers.sort((a, b) => a - b),
      description: strategies[i].desc,
      confidence: Math.floor(Math.random() * 20) + 70,
      strategy: strategies[i].name
    })
  }
  
  return games
} 